import type { ActionCandidate, InputCandidate } from "../../../types/candidate";
import { useRootStore } from "../../hooks";
import type {
  OutputDescription,
  ConditionsConfig_ConditionConfig,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { getOutputDescriptions } from "../../../utils/conditions-coverter.hook";
import {
  checkActionInput,
  isSmartdocFieldMissingNestedHrefOnly,
} from "../utils/inputValidation";
import { resolveReferencedConditionField } from "../../../containers/AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/left-source/resolve-referenced-condition-field";

const FIELD_MISSING_MESSAGE_KEY =
  "automation-form.deleted-field-validation-message";
const FIELD_MISSING_NESTED_URL_MESSAGE_KEY =
  "automation-form.deleted-field-validation-nested-url-link-message";

interface FieldMissingValidation {
  /** Empty string when valid; otherwise translated field-missing error text. */
  getFieldMissingErrorMessage: (
    input: InputCandidate,
    entity?: ActionCandidate,
    isCondition?: boolean,
  ) => string;

  /** Empty string when the condition field exists on outputs; otherwise translated error. */
  getConditionFieldMissingErrorMessage: (
    config: ConditionsConfig_ConditionConfig,
    entity?: ActionCandidate,
  ) => string;

  isInputValidByFieldMissing: (
    input: InputCandidate,
    entity?: ActionCandidate,
    isCondition?: boolean,
  ) => boolean;

  isConditionValidByFieldMissing: (
    config: ConditionsConfig_ConditionConfig,
    entity?: ActionCandidate,
  ) => boolean;
}

export const useFieldMissingValidation = (): FieldMissingValidation => {
  const rootStore = useRootStore();
  const candidate = rootStore.automations.candidate.self;
  const rootLooping = rootStore.automations.candidate.looping;

  const getFieldMissingErrorMessage = (
    input: InputCandidate,
    entity?: ActionCandidate,
    isCondition?: boolean,
  ): string => {
    const triggerOutputs = getOutputDescriptions(candidate.trigger);
    // Resolve currentItem references against the loop the action actually lives
    // in (parity with candidateActionInputReferenceValid / the field editors).
    // The root scope is wrong for an action nested in a loop (loop-in-loop or
    // loop-in-branch), so a valid current-item reference would be falsely
    // flagged "Please remove invalid values".
    const looping =
      rootLooping?.scopeForAction?.(entity?.hash ?? "") ?? rootLooping;
    const ok = checkActionInput(
      input,
      triggerOutputs,
      candidate,
      entity?.actionReference,
      isCondition,
      entity,
      looping,
    );
    if (ok) {
      return "";
    }
    if (
      isSmartdocFieldMissingNestedHrefOnly(
        input,
        triggerOutputs,
        candidate,
        entity,
        looping,
      )
    ) {
      return String(t(FIELD_MISSING_NESTED_URL_MESSAGE_KEY));
    }
    return String(t(FIELD_MISSING_MESSAGE_KEY));
  };

  const isInputValidByFieldMissing = (
    input: InputCandidate,
    entity?: ActionCandidate,
    isCondition?: boolean,
  ): boolean => getFieldMissingErrorMessage(input, entity, isCondition) === "";

  const isConditionValidByFieldMissing = (
    config: ConditionsConfig_ConditionConfig,
    action?: ActionCandidate,
  ): boolean => {
    // Reference-mode (CL branch) conditions store the left side as an
    // outputReference, not a conditionFieldId. Validate them by resolving the
    // reference to its source field -- the conditionFieldId-vs-outputs check
    // below never matches an empty conditionFieldId, so it would otherwise flag
    // every reference-mode condition as a missing field.
    if (config.outputReference !== undefined) {
      return (
        resolveReferencedConditionField(
          rootStore.automations.candidate,
          config.outputReference,
        ) !== undefined
      );
    }

    // No left side picked yet -- the shape addCondition seeds for a brand-new
    // row. "Field missing" means a CHOSEN field no longer exists, not that the
    // user hasn't chosen one, so an untouched row must not surface it. Left-side
    // completeness is enforced separately by the save gate
    // (branchConditionHasResolvableLeftSide -> "conditions-invalid").
    if (config.conditionFieldId === "") {
      return true;
    }

    const actionOutput =
      action !== undefined ? getOutputDescriptions(action, true) : [];
    const triggerOutputs = getOutputDescriptions(candidate.trigger, true);

    if (action !== undefined && actionOutput?.length === 0) {
      return true;
    }

    if (triggerOutputs.length === 0) {
      return true;
    }

    return !![...actionOutput, ...triggerOutputs].find(
      (o: OutputDescription) => o.outputId === config.conditionFieldId,
    );
  };

  const getConditionFieldMissingErrorMessage = (
    config: ConditionsConfig_ConditionConfig,
    action?: ActionCandidate,
  ): string =>
    isConditionValidByFieldMissing(config, action)
      ? ""
      : String(t(FIELD_MISSING_MESSAGE_KEY));

  return {
    getFieldMissingErrorMessage,
    getConditionFieldMissingErrorMessage,
    isInputValidByFieldMissing,
    isConditionValidByFieldMissing,
  };
};
